/* LAV TDMS — application shell: dark top bar + grouped sidebar. window.APP.Shell */
import { useState, useRef, useEffect, type CSSProperties, type ReactNode } from 'react'
import { qk, useMeta } from '@/data/queries'
import { useCommand } from '@/data/mutations'
import { useMe } from '@/data/meta'
import type { Notif, Project } from '@/data/types'
import { Avatar, toast, Tooltip, useDensity, useDropMaxHeight } from '@/ui'
import { Icon as BaseIcon } from '@/ds/icons'
import { LANGS, t, type Lang } from '@/i18n/lang'


// Icon wrapper supporting the prototype's `color` prop (lucide inherits currentColor).
function Icon({ name, size, color }: { name: string; size?: number; color?: string }) {
  if (color == null) return <BaseIcon name={name} size={size} />
  return (
    <span style={{ display: 'inline-flex', color }}>
      <BaseIcon name={name} size={size} />
    </span>
  )
}

export function Brandmark({ size = 26 }: { size?: number; onHeader?: boolean }) {
  return (
    <div style={{ width: size, height: size, flex: '0 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: size * 0.1, padding: size * 0.2, background: 'var(--amber-400)', borderRadius: 1 }}>
      <span style={{ height: size * 0.1, width: '100%', background: 'var(--slate-900)' }} />
      <span style={{ height: size * 0.1, width: '100%', background: 'rgba(28,37,48,.55)' }} />
      <span style={{ height: size * 0.1, width: '62%', background: 'rgba(28,37,48,.55)' }} />
    </div>
  )
}

/* area — раздел прав, без которого пункт не показывается. Скрытие само по
   себе ничего не защищает (сервер отбивает запрос в любом случае), но пункт,
   который гарантированно ответит 403, показывать незачем. Без area — пункт
   личный: «Мои действия» есть у каждого участника проекта. */
interface NavItem { id: string; icon: string; ru: string; en: string; area?: string }
interface NavGroup { group: { ru: string; en: string }; items: NavItem[] }

export const NAV: NavGroup[] = [
  { group: { ru: 'Рабочее место', en: 'Workspace' }, items: [
    { id: 'inbox', icon: 'inbox', ru: 'Мои действия', en: 'My actions' },
    { id: 'dashboard', icon: 'layout-dashboard', ru: 'Обзор проекта', en: 'Overview', area: 'reports' },
  ]},
  { group: { ru: 'Документация', en: 'Documents' }, items: [
    { id: 'mdr', icon: 'folder-tree', ru: 'Реестр MDR', en: 'MDR register', area: 'mdr' },
    { id: 'mdrdocs', icon: 'table-2', ru: 'Документы MDR', en: 'MDR documents', area: 'mdr' },
    { id: 'vdr', icon: 'package', ru: 'Реестр VDR / PO', en: 'VDR register', area: 'vdr' },
    { id: 'trm', icon: 'send', ru: 'Трансмитталы', en: 'Transmittals', area: 'trm' },
    /* Комплект ИД — про сдачу объекта, а не про партию подачи, поэтому стоит
       рядом с реестрами: его состав считается по ним. Право — MDR, потому что
       комплект и есть срез реестра по титулу. */
    { id: 'handover', icon: 'package-check', ru: 'Комплекты ИД', en: 'Handover packages', area: 'mdr' },
  ]},
  { group: { ru: 'Рассмотрение', en: 'Review' }, items: [
    { id: 'review', icon: 'clipboard-check', ru: 'Рассмотрение', en: 'Review', area: 'review' },
    { id: 'crs', icon: 'message-square-warning', ru: 'CRS / ACSR', en: 'Comments', area: 'crs' },
  ]},
  { group: { ru: 'Аналитика', en: 'Analytics' }, items: [
    /* Прогресс — отчётность о ходе работ, поэтому право на него то же, что на
       отчёты: разбивку правит администратор, читают все, кто отвечает «когда». */
    { id: 'progress', icon: 'layers', ru: 'Пакеты работ', en: 'Work packages', area: 'reports' },
    { id: 'reports', icon: 'bar-chart-3', ru: 'Отчёты и выгрузки', en: 'Reports', area: 'reports' },
    { id: 'search', icon: 'search', ru: 'Поиск', en: 'Search', area: 'mdr' },
  ]},
  { group: { ru: 'Администрирование', en: 'Admin' }, items: [
    { id: 'vendors', icon: 'building-2', ru: 'Поставщики', en: 'Vendors', area: 'mdr' },
    /* Журнал — свой раздел прав (V64): его читают те, кто разбирает
       происшествия, а не только те, кто заводит учётные записи. */
    { id: 'audit', icon: 'history', ru: 'Журнал аудита', en: 'Audit log', area: 'audit' },
    /* Экран администрирования показывается и тому, кто ведёт только
       справочники: вкладки внутри него роль разводит сама. */
    { id: 'admin', icon: 'shield-check', ru: 'Администрирование системы', en: 'System administration', area: 'dicts' },
  ]},
]

const CONTRACTOR_NAV: NavGroup[] = [
  { group: { ru: 'Портал подрядчика', en: 'Contractor portal' }, items: [
    { id: 'inbox', icon: 'inbox', ru: 'Мои задачи', en: 'My tasks' },
    /* Реестр своего контракта: подрядчик его наполняет — загружает состав и
       сроки, пока реестр не отправлен на согласование (Ф2). */
    { id: 'cmdr', icon: 'folder-tree', ru: 'Мой реестр MDR', en: 'My MDR register' },
    { id: 'cdocs', icon: 'table-2', ru: 'Мои документы', en: 'My documents' },
    { id: 'ctrm', icon: 'send', ru: 'Трансмитталы', en: 'Transmittals' },
    { id: 'ccrs', icon: 'message-square-warning', ru: 'Замечания', en: 'Comments' },
  ]},
]

function useOutside(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    function h(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [onClose, ref])
}

function ProjectMenu({ lang, onSwitchProject, onPick, onClose, allowed = [] }: { lang: Lang; onSwitchProject: () => void; onPick?: (id: string) => void; onClose: () => void; allowed?: string[] }) {
  const ref = useRef<HTMLDivElement>(null)
  useOutside(ref, onClose)
  /* Проектов у крупного участника десяток: список ограничивается окном, а не
     ростом своего содержимого. */
  const maxH = useDropMaxHeight(ref)
  // Список проектов ограничен теми, к которым у вошедшего есть доступ:
  // показатели чужого проекта — тоже сведения.
  const projects = useMeta<Project[]>('projects').data ?? []
  /* Назначение в БД шире токена: проект, выданный после входа, в claim ещё не
     попал. Переключение на него откатилось бы молча — вместо этого замок и
     объяснение (та же логика, что на экране выбора проекта). */
  const locked = (id: string) => allowed.length > 0 && !allowed.includes(id)
  return (
    /* Вход через menu-pop (tooltip-in): панель растёт от своего угла у кнопки,
       а не возникает из ниоткуда. Уход остаётся мгновенным намеренно —
       отложенный unmount (useOutside снимает state сразу) — отдельная правка. */
    <div ref={ref} className="menu-pop menu-pop--left" style={{ position: 'absolute', top: 38, left: 0, width: 320, maxHeight: maxH, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', zIndex: 'var(--z-overlay)' as unknown as number, overflowX: 'hidden', overflowY: 'auto' }}>
      <div style={{ padding: '8px 12px', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', borderBottom: '1px solid var(--border-subtle)' }}>{t(lang, 'Доступные проекты', 'Your projects')}</div>
      {projects.map((p) => (
        <button key={p.id} onClick={() => {
          if (locked(p.id)) {
            toast({
              tone: 'info',
              title: t(lang, `Проект ${p.id} ещё не в текущей сессии`, `Project ${p.id} is not in the current session yet`),
              text: t(lang, 'Доступ назначен, но токен выдан раньше. Выйдите и войдите заново.', 'Access is assigned, but the token predates it. Sign out and back in.'),
            })
            onClose()
            return
          }
          if (onPick) onPick(p.id); onClose()
        }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: p.active ? 'var(--accent-soft)' : 'transparent', cursor: 'pointer', textAlign: 'left', opacity: locked(p.id) ? 0.72 : undefined }} className="tdms-row">
          <span style={{ width: 8, height: 8, borderRadius: 999, background: `var(${p.color})`, flex: '0 0 auto' }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-strong)' }}>{p.id}</span>
            <span style={{ display: 'block', font: 'var(--font-ui)', fontSize: 12, color: 'var(--text-subtle)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
          </span>
          {locked(p.id) ? <Icon name="lock" size={13} color="var(--text-subtle)" /> : p.active ? <Icon name="check" size={15} color="var(--accent-ink)" /> : null}
        </button>
      ))}
      {/* Один проект — переключать нечего, и об этом честнее сказать, чем
          показывать список из одной строки как выбор. Список приходит с
          сервера в границах доступа; активный проект берётся из токена. */}
      {projects.length < 2 ? (
        <div style={{ padding: '8px 12px', font: 'var(--font-caption)', color: 'var(--text-subtle)', borderBottom: '1px solid var(--border-subtle)' }}>
          {t(lang, 'Доступен один проект. Остальные назначает администратор.', 'You have one project. Others are granted by the administrator.')}
        </div>
      ) : null}
      <button onClick={onSwitchProject} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 12px', border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }} className="tdms-row">
        <Icon name="grid-2x2" size={15} />{t(lang, 'Все проекты и выход', 'All projects / sign out')}
      </button>
    </div>
  )
}

function AccountMenu({ lang, persona, vendor, onSwitchProject, onSignOut, onNotifications, onSubstitutions, onClose }: { lang: Lang; persona: string; vendor?: string; onSwitchProject: () => void; onSignOut: () => void; onNotifications: () => void; onSubstitutions: () => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useOutside(ref, onClose)
  const maxH = useDropMaxHeight(ref)
  const isContractor = persona === 'contractor'
  // Личность — из токена и справочника проекта, а не из seed: раньше здесь
  // стоял захардкоженный адрес на случай, если у ME его нет.
  const me = useMe().data
  const name = isContractor ? vendor : (me?.user.name ?? '')
  const sub = isContractor
    ? t(lang, 'Подрядчик · внешний доступ', 'Contractor · external')
    : [me?.user.role, me?.user.email].filter(Boolean).join(' · ')
  const item = (icon: string, label: ReactNode, onClick: () => void, danger?: boolean) => (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', border: 0, background: 'transparent', cursor: 'pointer', color: danger ? 'var(--danger-ink)' : 'var(--text-body)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', textAlign: 'left' }} className="tdms-row">
      <Icon name={icon} size={15} color={danger ? 'var(--danger-ink)' : 'var(--text-subtle)'} />{label}
    </button>
  )
  return (
    /* Вход через menu-pop (origin top right — панель висит у правого края);
       уход мгновенный намеренно, см. ProjectMenu. */
    <div ref={ref} className="menu-pop" style={{ position: 'absolute', top: 38, right: 0, width: 264, maxHeight: maxH, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', zIndex: 'var(--z-overlay)' as unknown as number, overflowX: 'hidden', overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>
        <Avatar name={name} size={36} />
        <div style={{ minWidth: 0 }}>
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
        </div>
      </div>
      {/* «Профиль» и «Настройки аккаунта» отсюда убраны: за ними не стояло
          ничего, кроме тоста «раздел в разработке». Личность — имя, роль и
          почта — напечатана строкой выше, а настройки рабочего места (тема,
          язык, плотность строк, проект) стоят в самой шапке и в этом же меню.
          Пункт, который только извиняется, хуже отсутствующего.

          «Уведомления» остались и открывают ту самую панель, что и колокольчик
          рядом: это работающий пункт, а не заглушка. Выбор, какие извещения
          приходят на почту, живёт на экране администрирования («Мои
          извещения») и доступен тем, кого туда пускает роль. */}
      <div style={{ padding: '4px 0', borderBottom: '1px solid var(--border-subtle)' }}>
        {item('bell', t(lang, 'Уведомления', 'Notifications'), () => { onClose(); onNotifications() })}
        {/* Замещение на время отпуска или болезни заводит и сам человек — это
            передача права подписи по собственному решению. Пункт живёт здесь, а
            не только в администрировании: раздел администрирования показан
            лишь тем, у кого есть право на справочники, и рецензент до своей же
            вкладки не добрался бы. */}
        {vendor ? null : item('user-cog', t(lang, 'Моё замещение', 'My substitution'),
          () => { onClose(); onSubstitutions() })}
      </div>
      <div style={{ padding: '4px 0' }}>
        {item(isContractor ? 'log-out' : 'replace', isContractor ? t(lang, 'Сменить учётную запись', 'Switch account') : t(lang, 'Сменить проект', 'Switch project'), () => { onClose(); onSwitchProject() })}
        {item('log-out', t(lang, 'Выход', 'Sign out'), () => { onClose(); onSignOut() }, true)}
      </div>
    </div>
  )
}

/* Сервер отдаёт машинную отметку времени, а не подпись «10 минут назад»:
   подпись устаревает при первом же простое вкладки, поэтому считается здесь.
   Значение, которое не разбирается как дата, показывается как есть — так
   переживают переход демо-данные прототипа с их относительными строками. */
function ago(ts: string, lang: Lang): string {
  const at = Date.parse(ts)
  if (Number.isNaN(at)) return ts
  const min = Math.round((Date.now() - at) / 60000)
  if (min < 1) return t(lang, 'только что', 'just now')
  if (min < 60) return t(lang, `${min} мин назад`, `${min} min ago`)
  const hours = Math.round(min / 60)
  if (hours < 24) return t(lang, `${hours} ч назад`, `${hours} h ago`)
  return new Date(at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ru-RU')
}

/**
 * Куда уводит уведомление.
 *
 * <p>Строка колокольчика называла предмет — «назначен шаг по TRM-0010» — и на
 * этом заканчивалась: пакет человек искал руками через меню и список. Вид
 * события и есть указание, куда идти, а сторона решает, чьим экраном.
 *
 * <p>Предмет у видов разный: у шагов и писем это код трансмиттала, у ответа
 * подрядчика — код замечания, у просрочки — номер документа. Документ
 * открывается карточкой поверх текущего экрана, остальное — своим экраном с
 * выделенной записью.
 *
 * <p>{@code null} — уводить некуда: у строк до outbox (V20) предмета нет вовсе,
 * и ссылку рисовать не из чего.
 */
function notifTarget(event: string | null | undefined, ref: string | null | undefined,
                     contractor: boolean): string | null {
  if (!event || !ref) return null
  switch (event) {
    /* Предмет — код ТРАНСМИТТАЛА. */
    case 'step-assigned':
    case 'step-due':
    case 'step-overdue':
    case 'trm-received':
    case 'trm-due':
    case 'trm-accepted':
    case 'trm-rejected':
    case 'trm-cancelled':
    case 'trm-acknowledged':
    case 'crs-issued':
      return contractor ? `ctrm?trm=${encodeURIComponent(ref)}` : `review/${encodeURIComponent(ref)}`
    /* Предмет — код ЗАМЕЧАНИЯ. */
    case 'vendor-answered':
    case 'crs-due':
    case 'crs-reopened':
    case 'crs-rejected':
      return contractor ? `ccrs/${encodeURIComponent(ref)}` : `crs/${encodeURIComponent(ref)}`
    /* Предмет — НОМЕР ДОКУМЕНТА. */
    case 'doc-overdue':
    case 'doc-due':
      return contractor ? `cdocs?doc=${encodeURIComponent(ref)}` : `mdrdocs?doc=${encodeURIComponent(ref)}`
    /* Сводка отчёта: в предмете лежит текст, а не код, — уводить некуда. */
    default:
      return null
  }
}

/**
 * Текст со ссылкой на предмет.
 *
 * <p>Ссылкой становится САМ код внутри фразы, а не отдельная кнопка «перейти»:
 * человек читает «назначен шаг по TRM-0010» и жмёт на то, что читает. Если
 * кода в тексте не нашлось (текст собран иначе), ссылкой становится вся
 * строка — молча терять переход хуже, чем подчеркнуть лишнее.
 */
function withRefLink(text: string, ref: string | null | undefined, go: (() => void) | null): ReactNode {
  if (!go) return text
  const at = ref ? text.indexOf(ref) : -1
  const link = (body: string) => (
    <span role="link" tabIndex={0}
          onClick={(e) => { e.stopPropagation(); go() }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
          style={{ color: 'var(--text-link)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>
      {body}
    </span>
  )
  if (at < 0 || !ref) return link(text)
  return <>{text.slice(0, at)}{link(ref)}{text.slice(at + ref.length)}</>
}

function NotifPanel({ lang, contractor, onGo, onClose }: {
  lang: Lang
  /** Сторона подрядчика: те же события ведут на её экраны, а не на экраны заказчика. */
  contractor: boolean
  onGo: (to: string) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useOutside(ref, onClose)
  const maxH = useDropMaxHeight(ref)
  const notifs = useMeta<Notif[]>('notifs').data ?? []
  const dotColor: Record<string, string> = { info: '--blue-500', success: '--green-500', warning: '--amber-400', danger: '--red-500' }
  const unread = notifs.filter((n) => n.unread).length
  /* Без этой команды точка «непрочитано» не гаснет никогда: строки пишет
     доставка, а снимать признак было некому — счётчик рос до бесконечности. */
  const markRead = useCommand<{ ids: number[] }>({
    method: 'PUT',
    path: () => '/notify/read',
    body: (a) => a.ids,
    invalidate: () => [qk.meta('notifs')],
  })
  /* Переход закрывает панель и гасит точку «непрочитано»: строку открыли —
     непрочитанной она уже не является. */
  const goOf = (n: Notif) => {
    const to = notifTarget(n.event, n.ref, contractor)
    if (!to) return null
    return () => {
      if (n.unread) markRead.mutate({ ids: [n.id] })
      onClose()
      onGo(to)
    }
  }
  return (
    /* Вход через menu-pop (origin top right — панель у колокольчика справа);
       уход мгновенный намеренно, см. ProjectMenu. */
    <div ref={ref} className="menu-pop" style={{ position: 'absolute', top: 38, right: 0, width: 360, maxHeight: maxH, display: 'flex', flexDirection: 'column', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', zIndex: 'var(--z-overlay)' as unknown as number, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Уведомления', 'Notifications')}</span>
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{unread} {t(lang, 'новых', 'new')}</span>
        {unread ? (
          <button onClick={() => markRead.mutate({ ids: [] })} disabled={markRead.isPending}
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--accent-ink)' }}>
            {t(lang, 'Прочитать все', 'Mark all read')}
          </button>
        ) : null}
      </div>
      {/* Список отдаёт высоту первым: заголовок с «прочитать все» остаётся на
          месте, а строки прокручиваются. */}
      <div style={{ maxHeight: 360, minHeight: 0, overflow: 'auto' }}>
        {/* Пустая панель без слов читается как поломка. */}
        {notifs.length === 0 ? (
          <div style={{ padding: '22px 12px', textAlign: 'center', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
            {t(lang, 'Пока ничего нет', 'Nothing here yet')}
          </div>
        ) : null}
        {notifs.map((n) => (
          <div key={n.id} style={{ display: 'flex', gap: 10, padding: '11px 12px', borderBottom: '1px solid var(--border-subtle)', background: n.unread ? 'var(--surface-hover)' : 'transparent' }}>
            <span style={{ display: 'grid', placeItems: 'center', width: 28, height: 28, flex: '0 0 auto', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', color: `var(${dotColor[n.kind]})` }}><Icon name={n.icon} size={15} /></span>
            <div style={{ minWidth: 0, flex: 1 }}>
              {/* Ссылка ОДНА на строку: код ищется сначала в заголовке, и
                  только если его там нет — в теле. Иначе при ненайденном коде
                  подчёркивались обе строки разом. */}
              <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>
                {withRefLink(n.title, n.ref, goOf(n))}
              </div>
              {/* Тело у большинства извещений — ГОЛЫЙ предмет, тот же код, что
                  уже назван в заголовке. Печатать его второй раз незачем, а
                  печатать простым текстом вредно вдвойне: человек видит код
                  дважды, жмёт на нижнюю копию — и не происходит ничего. */}
              {n.ref && n.text && n.text.trim() === n.ref.trim() ? null : (
                <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                  {n.ref && n.title.includes(n.ref) ? n.text : withRefLink(n.text, n.ref, goOf(n))}
                </div>
              )}
              <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 2 }}>{ago(n.ts, lang)}</div>
            </div>
            {n.unread ? <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--accent)', flex: '0 0 auto', marginTop: 4 }} /> : null}
          </div>
        ))}
      </div>
    </div>
  )
}

interface ShellProps {
  active: string
  onNav: (id: string) => void
  theme: string
  onTheme: (theme: string) => void
  lang: Lang
  onLang: (lang: string) => void
  /** Боковое меню свёрнуто в полосу иконок. Состояние живёт в App и переживает перезагрузку. */
  railed: boolean
  onRailed: (railed: boolean) => void
  /** Открыть справку: сам подвал меню её не рисует — панель живёт рядом с прочими наложениями. */
  onHelp: () => void
  onSwitchProject: () => void
  onPickProject?: (id: string) => void
  /** Проекты из токена: назначение в БД шире, и такие пункты меню показываются с замком. */
  allowedProjects?: string[]
  onSignOut?: () => void
  children?: ReactNode
  counts?: Record<string, number | undefined>
  persona?: string
  vendor?: string
}

export function Shell({ active, onNav, theme, onTheme, lang, onLang, railed, onRailed, onHelp, onSwitchProject, onPickProject, allowedProjects, onSignOut, children, counts = {}, persona = 'owner', vendor }: ShellProps) {
  const [projOpen, setProjOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [acctOpen, setAcctOpen] = useState(false)
  /* Раскладка полосы отделена от ширины. При сворачивании ширина едет
     var(--duration-normal), а раскладка (подписи убраны, иконки 20 px, бейджи
     поверх) раньше включалась в первом же кадре — подписи исчезали и иконки
     прыгали ещё в полной ширине. Теперь сворачивание держит развёрнутую
     раскладку до конца перехода ширины (transitionend), а разворачивание
     меняет раскладку сразу — оно и без того было чистым. При reduced-motion
     переход 1 мс, и задержка неощутима. */
  const navRef = useRef<HTMLElement>(null)
  const [railLayout, setRailLayout] = useState(railed)
  useEffect(() => {
    if (!railed) { setRailLayout(false); return }
    const el = navRef.current
    if (!el) { setRailLayout(true); return }
    const done = (e: TransitionEvent) => {
      if (e.propertyName === 'width' && e.target === el) setRailLayout(true)
    }
    el.addEventListener('transitionend', done)
    /* Прерванный переход даёт transitioncancel вместо transitionend — без
       него раскладка застревала бы развёрнутой в узкой полосе. */
    el.addEventListener('transitioncancel', done)
    return () => {
      el.removeEventListener('transitionend', done)
      el.removeEventListener('transitioncancel', done)
    }
  }, [railed])
  const meQuery = useMe()
  const me = meQuery.data
  const projects = useMeta<Project[]>('projects').data ?? []
  const P = projects.find((p) => p.id === me?.project)
  const unread = (useMeta<Notif[]>('notifs').data ?? []).filter((n) => n.unread).length
  const isContractor = persona === 'contractor'
  const accountName = (isContractor ? vendor : me?.user.name) ?? ''
  /* Плотность строк действует на все таблицы сразу, поэтому переключатель стоит
     здесь, рядом с темой и языком, а не в панели реестра: на журнале аудита, в
     трансмитталах и на экранах подрядчика панели с фильтрами нет вовсе, и
     дотянуться до настройки было неоткуда. */
  const density = useDensity()
  /* Подпись у строки поиска обещала сочетание, которого не было, да ещё и
     маковским знаком на машине с Windows или Linux. Сочетание заведено, а
     подпись называет ту клавишу, которая есть у этой машины. */
  const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent)
  const searchHint = isMac ? '⌘K' : 'Ctrl+K'
  useEffect(() => {
    /* Код клавиши, а не символ: на русской раскладке та же клавиша даёт «л», и
       по символу сочетание работало бы через раз. */
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'KeyK' || e.altKey || e.shiftKey) return
      if (isMac ? !e.metaKey || e.ctrlKey : !e.ctrlKey || e.metaKey) return
      e.preventDefault()
      onNav('search')
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isMac, onNav])
  /* Пункты, которых роль не даёт, убираются вместе с опустевшими группами:
     заголовок «Администрирование» над пустотой выглядит поломкой.

     Пока права неизвестны — например, сервер недоступен — не убирается ничего.
     Скрытие здесь и так не защищает (отбивает сервер), а приложение без единого
     пункта навигации выглядит сломанным, а не ограниченным. */
  const allowed = (area?: string) =>
    !area || !meQuery.isSuccess || (me?.perms[area] != null && me.perms[area] !== '—')
  const navGroups = (isContractor ? CONTRACTOR_NAV : NAV)
    .map((g) => ({ ...g, items: g.items.filter((n) => allowed(n.area)) }))
    .filter((g) => g.items.length > 0)
  /* Ширина содержимого развёрнутого меню: 224 − 1 бордюр − 16 отступы − 10
     полоса прокрутки. Пока ширина едет 56 → 224, содержимое иначе переливается
     по месту: подписи переносятся по словам, строки прыгают в высоту и
     схлопываются обратно. С заданной шириной оно просто выезжает из-под
     overflowX: hidden. Взят узкий случай — со скроллбаром: шире, чем в
     установившемся состоянии, содержимое быть не должно. */
  const contentW = 'calc(var(--sidebar-w) - 27px)'
  /* Подвал выходит в боковые отступы (margin: 0 -8px), поэтому он на 16 шире. */
  const footerW = 'calc(var(--sidebar-w) - 11px)'
  /* «Справка и поддержка» остаётся подписью, а не кнопкой: нажимать нечего.
     В полосе от неё видна одна иконка, поэтому имя переезжает в aria-label,
     а расшифровка — в подсказку. */
  const helpLabel = t(lang, 'Справка и поддержка', 'Help & support')
  /* Подвал стоит НАД прокруткой, а не липнет в её потоке. Пятнадцать пунктов
     на окне ноутбука (видимая высота меню около 573 px при 726 нужных) не
     помещались, а липкий подвал с непрозрачным фоном наезжал на «Поиск» и
     срезал ему нижние 14 px. Теперь список прокручивается сам, а подвал —
     последняя строка меню и ничего не накрывает.
     В полосе иконок боковые отступы у меню урезаны до 4: с прежними −8 подвал
     оказывался на 8 px шире самой полосы и выступал за её границу. */
  /* Подвал был надписью: она называла справку, которой негде было открыться, —
     и правила («почему номер не принимается», «откуда объект строки») жили в
     голове внедренца. Теперь это кнопка, за ней руководство по функциям. */
  const helpItem = (
    <button type="button" onClick={onHelp} aria-label={railLayout ? helpLabel : undefined}
            style={{ flex: '0 0 auto', minWidth: railLayout ? undefined : footerW, width: railLayout ? undefined : 'auto', padding: '10px', margin: railLayout ? '0 -4px' : '0 -8px', border: 0, borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: railLayout ? 'center' : undefined, gap: 8, cursor: 'pointer', font: 'var(--font-caption)', color: 'var(--text-subtle)', background: 'var(--surface-card)', textAlign: 'left' }}
            className="tdms-rail">
      <Icon name="circle-help" size={railLayout ? 18 : 14} />{railLayout ? null : helpLabel}
    </button>
  )

  return (
    <div data-theme={theme} style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)', minHeight: 0 }}>
      {/* Top bar */}
      <header style={{ height: 'var(--topbar-h)', flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 14, padding: '0 14px', background: 'var(--surface-header)', borderBottom: '1px solid var(--border-header)', position: 'relative', zIndex: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <Brandmark />
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 14.5, letterSpacing: '.06em', color: 'var(--text-on-header)' }}>LAV</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 11.5, color: 'var(--amber-300)', letterSpacing: '.08em' }}>TDMS</span>
          </div>
          {isContractor ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 2, fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--blue-300, #9dc3f0)', background: 'rgba(80,140,220,.16)', border: '1px solid rgba(120,160,220,.4)', borderRadius: 3, padding: '2px 7px' }}><Icon name="external-link" size={11} />{t(lang, 'Портал подрядчика', 'Contractor portal')}</span> : null}
        </div>

        {/* Меню сворачивается в полосу иконок. Кнопка стоит у логотипа, а не
            внутри самого меню: в свёрнутом виде ей там не хватает ширины, а у
            края экрана она остаётся на одном месте в обоих состояниях.
            Сочетания клавиш нет намеренно — Ctrl/⌘+K уже занят поиском. */}
        {/* hdr-btn даёт hover-фон, press и transition; размеры и цвета кнопок
            шапки остаются в инлайновом iconBtn. */}
        <button onClick={() => onRailed(!railed)} aria-label={railed ? t(lang, 'Развернуть меню', 'Expand menu') : t(lang, 'Свернуть меню', 'Collapse menu')} aria-expanded={!railed} aria-controls="tdms-sidebar" className="hdr-btn" style={iconBtn}>
          <Icon name={railed ? 'panel-left-open' : 'panel-left-close'} size={15} />
        </button>

        {/* Project switcher */}
        <div style={{ position: 'relative' }}>
          {/* Фон открытого состояния совпадает с hover-фоном hdr-btn — при
              открытом меню hover ничего не перекрашивает. */}
          <button onClick={() => setProjOpen(v => !v)} className="hdr-btn" style={{ display: 'flex', alignItems: 'center', gap: 9, height: 32, padding: '0 11px', background: projOpen ? 'rgba(255,255,255,.06)' : 'transparent', border: '1px solid var(--border-header)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}>
            <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--green-400)', flex: '0 0 auto' }} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-on-header)' }}>{me?.project ?? ''}</span>
            <span style={{ font: 'var(--font-ui)', fontSize: 12.5, color: 'var(--slate-400)', maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{isContractor ? vendor : (P?.name ?? '')}</span>
            <span style={{ color: 'var(--slate-400)' }}><Icon name="chevrons-up-down" size={13} /></span>
          </button>
          {projOpen ? <ProjectMenu lang={lang} onSwitchProject={onSwitchProject} onPick={onPickProject} allowed={allowedProjects} onClose={() => setProjOpen(false)} /> : null}
        </div>

        {/* Global search */}
        <div style={{ flex: 1, maxWidth: 420, marginLeft: 4 }}>
          <button onClick={() => onNav('search')} className="hdr-btn" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 32, padding: '0 11px', background: 'rgba(255,255,255,.05)', border: '1px solid var(--border-header)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--slate-400)' }}>
            <Icon name="search" size={14} />
            <span style={{ font: 'var(--font-ui)', fontSize: 12.5 }}>{t(lang, 'Поиск по № документа, TRM, CRS…', 'Search documents, TRM, CRS…')}</span>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 10.5, padding: '1px 5px', border: '1px solid var(--border-header)', borderRadius: 3 }}>{searchHint}</span>
          </button>
        </div>

        <div style={{ flex: '0 0 auto', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', border: '1px solid var(--border-header)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
            {LANGS.map((l) => (
              /* Активному языку hdr-btn не даётся: его hover-фон перекрасил бы
                 янтарную заливку выбранного, а нажимать активный язык незачем. */
              <button key={l} onClick={() => onLang(l)} className={lang === l ? undefined : 'hdr-btn'} style={{ width: 32, height: 28, border: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', background: lang === l ? 'var(--amber-400)' : 'transparent', color: lang === l ? 'var(--slate-900)' : 'var(--slate-400)' }}>{l}</button>
            ))}
          </div>
          <button onClick={() => density.setCompact(!density.compact)} aria-pressed={density.compact}
                  aria-label={t(lang, 'Плотность', 'Density')}
                  title={density.compact ? t(lang, 'Плотные строки: включены', 'Compact rows: on') : t(lang, 'Плотные строки: выключены', 'Compact rows: off')}
                  className="hdr-btn" style={{ ...iconBtn, color: density.compact ? 'var(--amber-400)' : 'var(--slate-300)' }}>
            <Icon name="rows-3" size={15} />
          </button>
          <button onClick={() => onTheme(theme === 'dark' ? 'light' : 'dark')} aria-label="Тема" className="hdr-btn" style={iconBtn}>
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
          </button>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setNotifOpen(v => !v)} aria-label="Уведомления" className="hdr-btn" style={iconBtn}>
              <Icon name="bell" size={15} />
              {unread ? <span style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, borderRadius: 999, background: 'var(--amber-400)', border: '1.5px solid var(--surface-header)' }} /> : null}
            </button>
            {notifOpen ? <NotifPanel lang={lang} contractor={!!vendor} onGo={onNav} onClose={() => setNotifOpen(false)} /> : null}
          </div>
          <div style={{ position: 'relative', paddingLeft: 4 }}>
            <button onClick={() => setAcctOpen(v => !v)} title={accountName} className="hdr-btn" style={{ display: 'flex', alignItems: 'center', gap: 5, height: 32, padding: '0 4px 0 0', border: 0, background: 'transparent', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}>
              <Avatar name={accountName} size={28} />
              <span style={{ color: 'var(--slate-400)' }}><Icon name="chevron-down" size={13} /></span>
            </button>
            {acctOpen ? <AccountMenu lang={lang} persona={persona} vendor={vendor} onSwitchProject={onSwitchProject} onSignOut={onSignOut || onSwitchProject} onNotifications={() => setNotifOpen(true)} onSubstitutions={() => onNav('admin/subs')} onClose={() => setAcctOpen(false)} /> : null}
          </div>
        </div>
      </header>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Боковые отступы в полосе урезаны: пятнадцать пунктов на низком окне
            дают вертикальную прокрутку, а скроллбар здесь не наложенный (10 px),
            и от 56 кнопке остаётся 29 — иконка уезжает от центра полосы. */}
        {/* Ширина едет по railed сразу, а раскладка внутри — по railLayout:
            при сворачивании содержимое выезжает из-под overflow: hidden в
            развёрнутом виде и перестраивается только по концу перехода. */}
        <nav ref={navRef} id="tdms-sidebar" aria-label={t(lang, 'Основная навигация', 'Main navigation')} style={{ width: railed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)', transition: 'width var(--duration-normal) var(--ease-standard)', flex: '0 0 auto', background: 'var(--surface-card)', borderRight: '1px solid var(--border-default)', display: 'flex', flexDirection: 'column', padding: railLayout ? '8px 4px 0' : '8px 8px 0', overflow: 'hidden' }}>
          {/* Прокручивается список пунктов, а не всё меню вместе с подвалом. */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {navGroups.map((grp, gi) => (
            /* Заголовок группы в полосу не помещается, но без него пятнадцать
               иконок сливаются в один столбец — вместо подписи остаётся
               волосяная линия между группами. */
            <div key={gi} style={{ marginBottom: 6, borderTop: railLayout && gi > 0 ? '1px solid var(--border-subtle)' : undefined, paddingTop: railLayout && gi > 0 ? 6 : undefined }}>
              {railLayout ? null : <div style={{ minWidth: contentW, font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', padding: '10px 10px 5px' }}>{t(lang, grp.group.ru, grp.group.en)}</div>}
              {grp.items.map((n) => {
                const on = n.id === active
                const label = t(lang, n.ru, n.en)
                /* Число видно и в полосе, но подсказка называет его всё равно:
                   бейдж режется на 99+, а вопрос «сколько именно» задают и о
                   сотнях. */
                const railLabel = counts[n.id] != null ? `${label}: ${counts[n.id]}` : label
                const item = (
                  <button key={n.id} onClick={() => onNav(n.id)} aria-label={railLayout ? railLabel : undefined} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: railLayout ? 'center' : undefined, gap: 10, width: '100%', minWidth: railLayout ? undefined : contentW, padding: railLayout ? '9px 0' : '8px 10px', marginBottom: 1, border: 0, cursor: 'pointer', background: on ? 'var(--accent-soft)' : 'transparent', color: on ? 'var(--accent-ink)' : 'var(--text-muted)', boxShadow: on ? 'inset 2px 0 0 var(--accent)' : 'none', font: 'var(--font-ui)', fontSize: 13, fontWeight: on ? 600 : 400, textAlign: 'left', borderRadius: 'var(--radius-sm)' }} className="tdms-nav tdms-rail">
                    {/* В полосе иконка — единственное, что от пункта осталось:
                        подпись убрана, и по 16 px её приходилось узнавать
                        прищурившись. Развёрнутое меню оставлено как было: там
                        иконка идёт рядом с подписью и крупнее её быть не
                        должна. */}
                    <Icon name={n.icon} size={railLayout ? 20 : 16} color={on ? 'var(--accent-ink)' : 'var(--text-subtle)'} />
                    {railLayout ? null : <span style={{ flex: 1 }}>{label}</span>}
                    {/* В полосе от счётчика оставалась точка: «здесь есть работа»
                        она передавала, а сколько именно — приходилось узнавать
                        наведением на каждую иконку по очереди, хотя ради этих
                        чисел меню и держат на виду. Число стоит бейджем поверх
                        иконки: 22 px на «99+» помещаются в 48 px кнопки, ширину
                        полосы (56 px) он не трогает — он вне потока. */}
                    {counts[n.id] != null
                      ? railLayout
                        ? <span style={{ position: 'absolute', top: 2, right: 2, height: 13, minWidth: 13, padding: '0 3px', display: 'grid', placeItems: 'center', borderRadius: 999, background: 'var(--accent)', color: 'var(--on-accent)', fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, lineHeight: 1, fontFeatureSettings: '"tnum" 1' }}>{counts[n.id]! > 99 ? '99+' : counts[n.id]}</span>
                        : <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 600, color: on ? 'var(--accent-ink)' : 'var(--text-subtle)', background: on ? 'var(--accent-line)' : 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', padding: '0 5px', minWidth: 18, textAlign: 'center' }}>{counts[n.id]}</span>
                      : null}
                  </button>
                )
                /* Подсказка — не имя кнопки: скринридер её не читает, поэтому
                   в полосе имя стоит ещё и в aria-label. */
                return railLayout ? <Tooltip key={n.id} label={railLabel} side="right">{item}</Tooltip> : item
              })}
            </div>
          ))}
          </div>
          {railLayout ? <Tooltip label={helpLabel} side="right">{helpItem}</Tooltip> : helpItem}
        </nav>

        <main style={{ flex: 1, minWidth: 0, overflow: 'auto', position: 'relative' }}>{children}</main>
      </div>
      {/* Наведение под условие указателя, как везде: на касании оно
          срабатывает от тапа и липнет. Боковая навигация — единственная
          поверхность, живущая всю сессию, и залипшее наведение стоит рядом с
          настоящим выделением, отчего выбранными выглядят два пункта. */}
      <style>{`
        @media (hover: hover) and (pointer: fine) {
          .tdms-nav:hover { background: var(--surface-hover) !important; }
          .tdms-row:hover { background: var(--surface-hover) !important; }
        }
        /* Нажатие — вне условия указателя: press обязан отвечать и на касании.
           !important — тем же приёмом, что hover выше: у строк меню фон задан
           инлайном и иначе глушит правило. */
        .tdms-row:active { background: var(--surface-active) !important; }
      `}</style>
    </div>
  )
}

const iconBtn: CSSProperties = { position: 'relative', width: 30, height: 30, display: 'grid', placeItems: 'center', border: '1px solid var(--border-header)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--slate-300)', cursor: 'pointer' }
